import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';
import {
  CheckCircle, XCircle, Loader2, RefreshCw, Clock, Download,
  Search, Plus, Trash2, Edit2, Settings, Layers, Landmark, FileText,
  ChevronLeft, ChevronRight, X, Package
} from 'lucide-react';
import { ExportListButtons } from './ExportListButtons';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending: 'bg-[#FFEA00]/20 text-[#FFEA00] border-0',
  processing: 'bg-[#007AFF]/20 text-[#007AFF] border-0',
  completed: 'bg-[#00E676]/20 text-[#00E676] border-0',
  rejected: 'bg-[#FF1744]/20 text-[#FF1744] border-0',
};

function TabButton({ active, icon: Icon, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
        active
          ? 'bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30'
          : 'text-[#A0B4CC] hover:bg-white/5 border border-transparent'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

function StatusFilterButton({ active, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
        active
          ? 'bg-white/10 text-white border border-white/20'
          : 'text-[#5C7599] hover:text-[#A0B4CC] border border-transparent'
      }`}
    >
      {label}
    </button>
  );
}

import { Pagination, SearchBar, FilterSelect } from './ListControls';

// ============ TAB 1: WITHDRAWAL REQUESTS ============
function WithdrawalRequestsTab({ api }) {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [batchLoading, setBatchLoading] = useState(false);
  const [limit, setLimit] = useState(20);

  const fetchWithdrawals = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/admin/withdrawals?skip=${skip}&limit=${limit}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const { data } = await api.get(url);
      setWithdrawals(data.withdrawals || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch withdrawals:', error);
    } finally {
      setLoading(false);
    }
  }, [api, skip, statusFilter, search]);

  useEffect(() => { fetchWithdrawals(); }, [fetchWithdrawals]);
  useEffect(() => { setSkip(0); setSelected(new Set()); }, [search, statusFilter]);

  const processWithdrawal = async (id, approve) => {
    setProcessing(id);
    try {
      await api.post(`/api/admin/withdrawals/${id}/process?approve=${approve}`);
      fetchWithdrawals();
    } catch (error) {
      console.error('Failed:', error);
    } finally {
      setProcessing(null);
    }
  };

  const completeWithdrawal = async (id) => {
    setProcessing(id);
    try {
      await api.post(`/api/admin/withdrawals/${id}/complete`);
      fetchWithdrawals();
    } catch (error) {
      console.error('Failed:', error);
    } finally {
      setProcessing(null);
    }
  };

  // Auto-disburse via PayMongo (Money Movement). Only callable on pending rows
  // once the admin has enabled disbursements + configured wallet_id in settings.
  const disburseViaPayMongo = async (id) => {
    setProcessing(id);
    try {
      const { data } = await api.post(`/api/payouts/admin/disburse/${id}`);
      toast.success(`PayMongo wallet_transaction created: ${data.wallet_transaction_id?.slice(0, 12)}…`);
      fetchWithdrawals();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'PayMongo disbursement failed');
    } finally {
      setProcessing(null);
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (selected.size === withdrawals.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(withdrawals.map(w => w.id)));
    }
  };

  const createBatch = async () => {
    const ids = Array.from(selected).filter(id => withdrawals.find(w => w.id === id && w.status === 'pending'));
    if (ids.length === 0) { toast.warning('Select pending withdrawals to batch'); return; }
    setBatchLoading(true);
    try {
      await api.post('/api/admin/withdrawals/batch/create', { withdrawal_ids: ids });
      toast.success('Batch created successfully');
      setSelected(new Set());
      fetchWithdrawals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create batch');
    } finally {
      setBatchLoading(false);
    }
  };

  const exportSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) { toast.warning('Select withdrawals to export'); return; }
    try {
      const response = await api.get(`/api/admin/withdrawals/export-selected?ids=${ids.join(',')}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'withdrawals_export.csv'; a.click();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 2 }).format(n || 0);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-[#5C7599] text-xs">Filter:</span>
        {[{ v: '', l: 'All' }, { v: 'pending', l: 'Pending' }, { v: 'processing', l: 'Processing' }, { v: 'completed', l: 'Completed' }, { v: 'rejected', l: 'Rejected' }].map(f => (
          <StatusFilterButton key={f.v} active={statusFilter === f.v} label={f.l} onClick={() => setStatusFilter(f.v)} />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or ID..."
            className="pl-10 bg-[#041A3B] border-white/10 text-white placeholder:text-[#5C7599] h-9" data-testid="withdrawal-search" />
        </div>
        <Button onClick={exportSelected} disabled={selected.size === 0}
          className="bg-[#00E676]/20 text-[#00E676] hover:bg-[#00E676]/30 border border-[#00E676]/30"
          data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-2" />
          Export CSV ({selected.size})
        </Button>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-[#00E5FF]/5 border border-[#00E5FF]/20">
          <span className="text-[#00E5FF] text-sm">{selected.size} selected</span>
          <Button size="sm" onClick={createBatch} disabled={batchLoading}
            className="bg-[#007AFF] text-white hover:bg-[#007AFF]/80" data-testid="create-batch-btn">
            {batchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4 mr-1" />}
            Create Batch
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="text-[#5C7599]">Clear</Button>
        </div>
      )}

      <Card className="bg-[#041A3B] border-white/10">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF] mx-auto" /></div>
          ) : withdrawals.length === 0 ? (
            <div className="py-12 text-center">
              <Clock className="w-12 h-12 text-[#5C7599] mx-auto mb-4" />
              <p className="text-[#A0B4CC]">No withdrawal requests found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="w-10">
                      <input type="checkbox" checked={selected.size === withdrawals.length && withdrawals.length > 0}
                        onChange={toggleSelectAll} className="rounded border-white/20" />
                    </TableHead>
                    <TableHead className="text-[#A0B4CC]">Member</TableHead>
                    <TableHead className="text-[#A0B4CC]">Bank</TableHead>
                    <TableHead className="text-[#A0B4CC]">Account</TableHead>
                    <TableHead className="text-[#A0B4CC]">Amount</TableHead>
                    <TableHead className="text-[#A0B4CC]">Fee</TableHead>
                    <TableHead className="text-[#A0B4CC]">Net</TableHead>
                    <TableHead className="text-[#A0B4CC]">Date</TableHead>
                    <TableHead className="text-[#A0B4CC]">Status</TableHead>
                    <TableHead className="text-[#A0B4CC] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {withdrawals.map((w) => (
                    <TableRow key={w.id} className="border-white/5 hover:bg-white/5">
                      <TableCell>
                        <input type="checkbox" checked={selected.has(w.id)} onChange={() => toggleSelect(w.id)} className="rounded border-white/20" />
                      </TableCell>
                      <TableCell>
                        <div className="text-white text-sm font-medium">{w.user_name || 'N/A'}</div>
                        <div className="text-[#5C7599] text-xs">{w.user_email || w.user_id?.substring(0, 12)}</div>
                      </TableCell>
                      <TableCell className="text-[#A0B4CC] text-sm">{w.bank_name || w.method || 'N/A'}</TableCell>
                      <TableCell>
                        <div className="text-white text-sm">{w.bank_account_name || 'N/A'}</div>
                        <div className="text-[#5C7599] text-xs font-mono">{w.bank_account_number || w.account_details?.number || 'N/A'}</div>
                      </TableCell>
                      <TableCell className="text-[#00E676] font-mono text-sm">{fmt(w.amount)}</TableCell>
                      <TableCell className="text-[#FF1744] font-mono text-xs">{w.fee_amount ? `-${fmt(w.fee_amount)}` : '-'}</TableCell>
                      <TableCell className="text-white font-mono text-sm">{w.net_amount ? fmt(w.net_amount) : fmt(w.amount)}</TableCell>
                      <TableCell className="text-[#5C7599] text-xs">{fmtDate(w.created_at)}</TableCell>
                      <TableCell><Badge className={STATUS_COLORS[w.status] || 'bg-white/10 text-white border-0'}>{w.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {w.status === 'pending' && (
                            <>
                              <Button size="sm" onClick={() => disburseViaPayMongo(w.id)} disabled={processing === w.id}
                                className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/80 h-7 px-2 text-[10px]"
                                title="Send via PayMongo Disbursement" data-testid={`paymongo-disburse-${w.id}`}>
                                {processing === w.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'PayMongo'}
                              </Button>
                              <Button size="sm" onClick={() => processWithdrawal(w.id, true)} disabled={processing === w.id}
                                className="bg-[#00E676] text-[#000B18] hover:bg-[#00E676]/80 h-7 px-2"
                                title="Mark as manually approved" data-testid={`approve-${w.id}`}>
                                {processing === w.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => processWithdrawal(w.id, false)} disabled={processing === w.id}
                                className="text-[#FF1744] hover:bg-[#FF1744]/10 h-7 px-2" data-testid={`reject-${w.id}`}>
                                <XCircle className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                          {w.status === 'processing' && (
                            <Button size="sm" onClick={() => completeWithdrawal(w.id)} disabled={processing === w.id}
                              className="bg-[#00E5FF]/20 text-[#00E5FF] hover:bg-[#00E5FF]/30 h-7 px-2 text-xs">
                              {processing === w.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Complete'}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <Pagination skip={skip} limit={limit} total={total} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />
        </CardContent>
      </Card>
    </>
  );
}

// ============ TAB 2: BATCHES ============
function BatchesTab({ api }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [processing, setProcessing] = useState(null);
  const [limit, setLimit] = useState(20);

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/admin/withdrawals/batches?skip=${skip}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const { data } = await api.get(url);
      setBatches(data.batches || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    } finally {
      setLoading(false);
    }
  }, [api, skip, search]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);
  useEffect(() => { setSkip(0); }, [search]);

  const completeBatch = async (batchId) => {
    setProcessing(batchId);
    try {
      await api.post(`/api/admin/withdrawals/batch/${batchId}/complete`);
      fetchBatches();
    } catch (error) {
      console.error('Failed:', error);
    } finally {
      setProcessing(null);
    }
  };

  const exportBatch = async (batchId) => {
    try {
      const response = await api.get(`/api/admin/withdrawals/batch/${batchId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = `batch_${batchId}.csv`; a.click();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 2 }).format(n || 0);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search Batch ID..."
            className="pl-10 bg-[#041A3B] border-white/10 text-white placeholder:text-[#5C7599] h-9" data-testid="batch-search" />
        </div>
      </div>

      <Card className="bg-[#041A3B] border-white/10">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF] mx-auto" /></div>
          ) : batches.length === 0 ? (
            <div className="py-12 text-center">
              <Layers className="w-12 h-12 text-[#5C7599] mx-auto mb-4" />
              <p className="text-[#A0B4CC]">No batches found</p>
              <p className="text-[#5C7599] text-xs mt-1">Select withdrawal requests and create a batch to process them</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-[#A0B4CC]">Batch Name</TableHead>
                    <TableHead className="text-[#A0B4CC]">Items</TableHead>
                    <TableHead className="text-[#A0B4CC]">Total Amount</TableHead>
                    <TableHead className="text-[#A0B4CC]">Status</TableHead>
                    <TableHead className="text-[#A0B4CC]">Created</TableHead>
                    <TableHead className="text-[#A0B4CC] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map(b => (
                    <TableRow key={b.id} className="border-white/5 hover:bg-white/5">
                      <TableCell className="text-white font-medium">{b.name}</TableCell>
                      <TableCell className="text-[#A0B4CC]">{b.count} withdrawals</TableCell>
                      <TableCell className="text-[#00E676] font-mono">{fmt(b.total_amount)}</TableCell>
                      <TableCell><Badge className={STATUS_COLORS[b.status] || 'bg-white/10 text-white border-0'}>{b.status}</Badge></TableCell>
                      <TableCell className="text-[#5C7599] text-xs">{fmtDate(b.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => exportBatch(b.id)}
                            className="text-[#00E676] hover:bg-[#00E676]/10 h-7 px-2" data-testid={`export-batch-${b.id}`}>
                            <Download className="w-3 h-3 mr-1" /> CSV
                          </Button>
                          {b.status === 'processing' && (
                            <Button size="sm" onClick={() => completeBatch(b.id)} disabled={processing === b.id}
                              className="bg-[#00E5FF]/20 text-[#00E5FF] hover:bg-[#00E5FF]/30 h-7 px-2 text-xs">
                              {processing === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Complete Batch'}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <Pagination skip={skip} limit={limit} total={total} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />
        </CardContent>
      </Card>
    </>
  );
}

// ============ TAB 3: MANAGE BANKS ============
function ManageBanksTab({ api }) {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newBank, setNewBank] = useState('');
  const [editBank, setEditBank] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchBanks = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/admin/banks?limit=200`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const { data } = await api.get(url);
      setBanks(data.banks || []);
    } catch (error) {
      console.error('Failed:', error);
    } finally {
      setLoading(false);
    }
  }, [api, search]);

  useEffect(() => { fetchBanks(); }, [fetchBanks]);

  const addBank = async () => {
    if (!newBank.trim()) return;
    setSaving(true);
    try {
      await api.post('/api/admin/banks', { name: newBank.trim() });
      setNewBank('');
      setShowAdd(false);
      fetchBanks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const updateBank = async () => {
    if (!editBank) return;
    setSaving(true);
    try {
      await api.put(`/api/admin/banks/${editBank.id}`, { name: editBank.name, is_active: editBank.is_active });
      toast.success('Bank updated');
      setEditBank(null);
      fetchBanks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteBank = async (id) => {
    if (!window.confirm('Delete this bank?')) return;
    try {
      await api.delete(`/api/admin/banks/${id}`);
      fetchBanks();
    } catch (error) {
      console.error('Failed:', error);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search banks..."
            className="pl-10 bg-[#041A3B] border-white/10 text-white placeholder:text-[#5C7599] h-9" data-testid="bank-search" />
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/80" data-testid="add-bank-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Bank
        </Button>
      </div>

      {/* Add Bank Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#041A3B] border-white/10 text-white">
          <DialogHeader><DialogTitle>Add New Bank</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white">Bank Name</Label>
              <Input value={newBank} onChange={e => setNewBank(e.target.value)} placeholder="Enter bank name..."
                className="bg-[#000B18] border-white/20 text-white" data-testid="new-bank-name" />
            </div>
            <Button onClick={addBank} disabled={saving} className="w-full bg-[#00E5FF] text-[#000B18]" data-testid="save-bank-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Bank'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Bank Dialog */}
      <Dialog open={!!editBank} onOpenChange={() => setEditBank(null)}>
        <DialogContent className="bg-[#041A3B] border-white/10 text-white">
          <DialogHeader><DialogTitle>Edit Bank</DialogTitle></DialogHeader>
          {editBank && (
            <div className="space-y-4">
              <div>
                <Label className="text-white">Bank Name</Label>
                <Input value={editBank.name} onChange={e => setEditBank({...editBank, name: e.target.value})}
                  className="bg-[#000B18] border-white/20 text-white" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editBank.is_active} onChange={e => setEditBank({...editBank, is_active: e.target.checked})} />
                <Label className="text-white">Active</Label>
              </div>
              <Button onClick={updateBank} disabled={saving} className="w-full bg-[#00E5FF] text-[#000B18]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Card className="bg-[#041A3B] border-white/10">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF] mx-auto" /></div>
          ) : (
            <div className="max-h-[500px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 sticky top-0 bg-[#041A3B]">
                    <TableHead className="text-[#A0B4CC]">Bank Name</TableHead>
                    <TableHead className="text-[#A0B4CC]">Status</TableHead>
                    <TableHead className="text-[#A0B4CC] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {banks.map(b => (
                    <TableRow key={b.id} className="border-white/5 hover:bg-white/5">
                      <TableCell className="text-white">{b.name}</TableCell>
                      <TableCell>
                        <Badge className={b.is_active ? 'bg-[#00E676]/20 text-[#00E676] border-0' : 'bg-[#FF1744]/20 text-[#FF1744] border-0'}>
                          {b.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setEditBank(b)} className="text-[#A0B4CC] h-7 px-2">
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteBank(b.id)} className="text-[#FF1744] h-7 px-2">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      <p className="text-[#5C7599] text-xs mt-2">{banks.length} banks configured</p>
    </>
  );
}

// ============ TAB 4: SETTINGS ============
function WithdrawalSettingsTab({ api }) {
  const [settings, setSettings] = useState({
    min_withdrawal: 100,
    max_withdrawal: 20000,
    processing_fee: 20,
    fee_type: 'fixed',
    withdrawals_enabled: true,
    processing_days_min: 3,
    processing_days_max: 5
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/config/withdrawal-settings');
        setSettings(prev => ({ ...prev, ...data }));
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [api]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/config/withdrawal-settings', settings);
      toast.success('Settings saved successfully');
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(n || 0);

  if (loading) return <div className="py-12 text-center"><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF] mx-auto" /></div>;

  return (
    <Card className="bg-[#041A3B] border-white/10 max-w-2xl">
      <CardContent className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-white text-lg font-semibold font-outfit">Withdrawal Settings</h3>
            <p className="text-[#5C7599] text-sm">Configure withdrawal limits and fees</p>
          </div>
          <Button onClick={save} disabled={saving} className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/80" data-testid="save-withdrawal-settings">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Settings className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>
        </div>

        <div className="h-px bg-white/10" />

        {/* Min/Max Withdrawal */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <Label className="text-white font-medium">Minimum Withdrawal Amount (PHP)</Label>
            <Input type="number" value={settings.min_withdrawal}
              onChange={e => setSettings({...settings, min_withdrawal: Number(e.target.value)})}
              className="bg-[#000B18] border-white/20 text-white mt-2 text-lg h-12" data-testid="min-withdrawal-input" />
            <p className="text-[#5C7599] text-xs mt-1.5">Members must have at least this amount to withdraw</p>
          </div>
          <div>
            <Label className="text-white font-medium">Maximum Withdrawal Amount (PHP)</Label>
            <Input type="number" value={settings.max_withdrawal}
              onChange={e => setSettings({...settings, max_withdrawal: Number(e.target.value)})}
              className="bg-[#000B18] border-white/20 text-white mt-2 text-lg h-12" data-testid="max-withdrawal-input" />
            <p className="text-[#5C7599] text-xs mt-1.5">Maximum amount per withdrawal request</p>
          </div>
        </div>

        {/* Processing Fee & Enabled */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <Label className="text-white font-medium">Processing Fee</Label>
            <div className="flex items-center gap-2 mt-2">
              <Input type="number" value={settings.processing_fee}
                onChange={e => setSettings({...settings, processing_fee: Number(e.target.value)})}
                className="bg-[#000B18] border-white/20 text-white text-lg h-12 flex-1" data-testid="processing-fee-input" />
              <div className="flex rounded-lg overflow-hidden border border-white/20">
                <button
                  onClick={() => setSettings({...settings, fee_type: 'fixed'})}
                  data-testid="fee-type-fixed"
                  className={`px-3 py-2.5 text-sm font-medium transition-all ${
                    settings.fee_type === 'fixed'
                      ? 'bg-white/15 text-white'
                      : 'bg-[#000B18] text-[#5C7599] hover:text-[#A0B4CC]'
                  }`}>
                  PHP Fixed
                </button>
                <button
                  onClick={() => setSettings({...settings, fee_type: 'percentage'})}
                  data-testid="fee-type-percentage"
                  className={`px-3 py-2.5 text-sm font-medium transition-all ${
                    settings.fee_type === 'percentage'
                      ? 'bg-white/15 text-white'
                      : 'bg-[#000B18] text-[#5C7599] hover:text-[#A0B4CC]'
                  }`}>
                  % Percentage
                </button>
              </div>
            </div>
            <p className="text-[#5C7599] text-xs mt-1.5">Fee deducted from withdrawal amount</p>
          </div>

          <div>
            <Label className="text-white font-medium">Withdrawals Enabled</Label>
            <div className="mt-3 flex items-center gap-3">
              <button
                onClick={() => setSettings({...settings, withdrawals_enabled: !settings.withdrawals_enabled})}
                data-testid="withdrawals-enabled-toggle"
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  settings.withdrawals_enabled ? 'bg-[#00E5FF]' : 'bg-[#5C7599]/40'
                }`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                  settings.withdrawals_enabled ? 'left-[26px]' : 'left-0.5'
                }`} />
              </button>
              <span className={`text-sm font-medium ${settings.withdrawals_enabled ? 'text-[#00E676]' : 'text-[#FF1744]'}`}>
                {settings.withdrawals_enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <p className="text-[#5C7599] text-xs mt-1.5">Disable to prevent all new withdrawal requests</p>
          </div>
        </div>

        {/* Processing Days */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <Label className="text-white font-medium">Processing Days (Min)</Label>
            <Input type="number" value={settings.processing_days_min}
              onChange={e => setSettings({...settings, processing_days_min: Number(e.target.value)})}
              className="bg-[#000B18] border-white/20 text-white mt-2 h-12" />
          </div>
          <div>
            <Label className="text-white font-medium">Processing Days (Max)</Label>
            <Input type="number" value={settings.processing_days_max}
              onChange={e => setSettings({...settings, processing_days_max: Number(e.target.value)})}
              className="bg-[#000B18] border-white/20 text-white mt-2 h-12" />
          </div>
        </div>

        <div className="h-px bg-white/10" />

        {/* Current Settings Summary */}
        <div className="rounded-lg bg-[#000B18] border border-white/10 p-4">
          <h4 className="text-[#A0B4CC] text-sm font-medium mb-3">Current Settings Summary</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-[#5C7599] text-xs">Min Amount</p>
              <p className="text-white font-semibold">{fmt(settings.min_withdrawal)}</p>
            </div>
            <div>
              <p className="text-[#5C7599] text-xs">Max Amount</p>
              <p className="text-white font-semibold">{fmt(settings.max_withdrawal)}</p>
            </div>
            <div>
              <p className="text-[#5C7599] text-xs">Fee</p>
              <p className="text-white font-semibold">
                {settings.fee_type === 'percentage' ? `${settings.processing_fee}%` : fmt(settings.processing_fee)}
              </p>
            </div>
            <div>
              <p className="text-[#5C7599] text-xs">Status</p>
              <p className={`font-semibold ${settings.withdrawals_enabled ? 'text-[#00E676]' : 'text-[#FF1744]'}`}>
                {settings.withdrawals_enabled ? 'Active' : 'Disabled'}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ MAIN PAGE ============
export function AdminWithdrawalsPage() {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('requests');

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-start sm:items-center justify-between gap-3 mb-6 flex-wrap">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white" data-testid="withdrawals-page-title">Withdrawal Requests</h1>
            <p className="text-[#A0B4CC] mt-1">Manage withdrawal requests, batches, and bank options</p>
          </div>
          <ExportListButtons api={api} resource="withdrawals" filename="withdrawals" />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="withdrawal-tabs">
          <TabButton active={activeTab === 'requests'} icon={FileText} label="Withdrawal Requests" onClick={() => setActiveTab('requests')} testId="tab-requests" />
          <TabButton active={activeTab === 'batches'} icon={Layers} label="Batches" onClick={() => setActiveTab('batches')} testId="tab-batches" />
          <TabButton active={activeTab === 'banks'} icon={Landmark} label="Manage Banks" onClick={() => setActiveTab('banks')} testId="tab-banks" />
          <TabButton active={activeTab === 'settings'} icon={Settings} label="Settings" onClick={() => setActiveTab('settings')} testId="tab-settings" />
        </div>

        {/* Tab Content */}
        {activeTab === 'requests' && <WithdrawalRequestsTab api={api} />}
        {activeTab === 'batches' && <BatchesTab api={api} />}
        {activeTab === 'banks' && <ManageBanksTab api={api} />}
        {activeTab === 'settings' && <WithdrawalSettingsTab api={api} />}
      </div>
    </AdminSidebarLayout>
  );
}
